<template>
  <section class="mx-auto flex min-h-[70vh] max-w-3xl flex-col gap-8 px-6 py-16">
    <div class="rounded-3xl border border-white/10 bg-midnight-800/60 p-6 shadow-glow sm:p-8">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('changePassword.eyebrow') }}</p>
          <h1 class="mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            {{ $t('changePassword.title') }}
          </h1>
          <p class="mt-3 max-w-xl text-sm text-steel-300">
            {{ $t('changePassword.description') }}
          </p>
        </div>
        <router-link
          to="/profile"
          class="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:border-white/30 hover:bg-white/10"
        >
          {{ $t('changePassword.backToSettings') }}
        </router-link>
      </div>

      <form class="mt-8 space-y-5" @submit.prevent="changePassword">
        <label class="block text-sm text-steel-200">
          {{ $t('changePassword.currentPassword') }}
          <input
            v-model="currentPassword"
            type="password"
            placeholder="Current password"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            autocomplete="current-password"
          />
        </label>

        <label class="block text-sm text-steel-200">
          {{ $t('changePassword.newPassword') }}
          <input
            v-model="newPassword"
            type="password"
            placeholder="New password"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            autocomplete="new-password"
          />
        </label>

        <label class="block text-sm text-steel-200">
          {{ $t('changePassword.repeatPassword') }}
          <input
            v-model="repeatPassword"
            type="password"
            placeholder="Repeat new password"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            autocomplete="new-password"
          />
        </label>

        <div class="rounded-2xl border border-white/10 bg-midnight-900/40 px-4 py-4 text-sm text-steel-300">
          {{ $t('changePassword.accountEmail') }}: <span class="text-white">{{ email }}</span>
        </div>

        <button
          type="submit"
          class="w-full rounded-2xl bg-white px-4 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="isLoading"
        >
          {{ isLoading ? $t('changePassword.submitting') : $t('changePassword.submit') }}
        </button>
      </form>

      <p v-if="statusMessage" class="mt-6 rounded-2xl border border-neon-400/30 bg-neon-400/10 px-4 py-3 text-sm text-neon-100">
        {{ statusMessage }}
      </p>

      <p v-if="errorMessage" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ errorMessage }}
      </p>
    </div>
  </section>
</template>

<script>
import { supabase } from '@/lib/supabase'

export default {
  name: 'ChangePasswordView',
  data() {
    return {
      email: '',
      currentPassword: '',
      newPassword: '',
      repeatPassword: '',
      isLoading: false,
      statusMessage: '',
      errorMessage: '',
    }
  },
  async mounted() {
    const { data } = await supabase.auth.getSession()
    const user = data?.session?.user
    if (!user) {
      this.$router.replace('/login')
      return
    }
    this.email = user.email || ''
  },
  methods: {
    async changePassword() {
      if (!this.email) {
        this.errorMessage = this.$t('changePassword.missingEmail')
        return
      }
      if (!this.currentPassword || !this.newPassword || !this.repeatPassword) {
        this.errorMessage = this.$t('changePassword.missingFields')
        return
      }
      if (this.newPassword.length < 6) {
        this.errorMessage = this.$t('changePassword.minLength')
        return
      }
      if (this.newPassword !== this.repeatPassword) {
        this.errorMessage = this.$t('changePassword.passwordMismatch')
        return
      }
      if (this.currentPassword === this.newPassword) {
        this.errorMessage = this.$t('changePassword.samePassword')
        return
      }

      this.isLoading = true
      this.statusMessage = ''
      this.errorMessage = ''

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: this.email,
        password: this.currentPassword,
      })

      if (signInError) {
        this.isLoading = false
        this.errorMessage = this.$t('changePassword.incorrectCurrent')
        return
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: this.newPassword,
      })

      this.isLoading = false

      if (updateError) {
        this.errorMessage = updateError.message || this.$t('changePassword.updateError')
        return
      }

      this.currentPassword = ''
      this.newPassword = ''
      this.repeatPassword = ''
      this.statusMessage = this.$t('changePassword.updated')
    },
  },
}
</script>
